/**
 Created 13.10.2023
 A file to store some useful framework-agnostic functions
 */

import moment from 'moment-timezone';
import { ref } from 'vue';
import Cookies from 'js-cookie';
import { notify } from 'notiwind';

// Create an accessible ref to store the current language
export const currentLanguage = ref('');
// Create an accessible ref to store the current locale
export const currentLocale = ref('');

// Set a flag to check if the language has been initialized
let languageInitialized = false;

// Map language codes to locale codes
const languageLocaleMapper = {
  no: 'nb-NO',
  sv: 'sv-SE',
  da: 'da-DK',
  en: 'en-GB',
};
// Map language codes to moment.js locale codes
const languageMomentMapper = {
  no: 'nb',
  sv: 'sv',
  da: 'da',
  en: 'en-gb',
};

/**
 * Function to set moment.js and JS locale formats from simple language codes
 * @param {string} value - simple language code (no, sv, da, en)
 */
function setLocaleFromCurrentLanguage(value) {
  moment.locale(languageMomentMapper[value] ?? 'nb');
  currentLocale.value = languageLocaleMapper[value] ?? 'nb-NO';
}

/**
 * Set a cookie for the selected language
 * @param {string} value - language code (no, sv, da, en)
 * @returns {boolean} - true if language was set, false if not
 */
export function setCurrentLanguage(value) {
  // Check if the value is a valid language code
  const allowedLanguages = ['no', 'sv', 'da', 'en'];

  if (!allowedLanguages.includes(value)) {
    notify({
      type: 'danger',
      title: 'Language could not be set',
      text: 'Seems you tried an unsupported language code. Please try again.',
    }, 4000);

    return false;
  }

  currentLanguage.value = value;
  setLocaleFromCurrentLanguage(value);
  Cookies.set('currentLanguage', value, {
    expires: 30, secure: false, sameSite: 'lax',
  });

  notify({
    type: 'success',
    title: 'Language changed!',
  }, 1500);

  return true;
}

/**
 * Function to get current language from cookie, or default to 'no'
 * @returns {string} - current language
 */
export function getCurrentLanguage() {
  // Check if the cookie exists, and if not, set it to 'no'
  if (!Cookies.get('currentLanguage')) {
    setCurrentLanguage('no');
  }
  // Get the current language from the cookie
  currentLanguage.value = Cookies.get('currentLanguage');
  // Set moment locale based on current language
  setLocaleFromCurrentLanguage(currentLanguage.value);

  return currentLanguage.value;
}

/**
 * Function to initiate state where locale and language is set based on the environment.
 */
export function initiateLanguageLocale() {
  if (languageInitialized) {
    return;
  }
  getCurrentLanguage();
  languageInitialized = true;
}

/**
 * Function to check if a date is valid in MomentJS and return the date
 * @param {string|Date} date - date to check
 * @returns {string|Date} - valid date or error message
 */
function isValidMomentDate(date) {
  if (!moment(date).isValid()) {
    throw new Error('Invalid date');
  }

  return date;
}

/**
 * Function to get the date and time in a specific timezone
 * @param {moment.Moment|string} inputDate - date to convert, e.g. '2023-10-13T12:00'
 * @param {string} timezone - timezone to convert to, e.g. 'Europe/Oslo'
 * @param {string} returnString - returns formatted date and time if filled, e.g. 'LL' or 'DD.MM.YYYY HH:mm'
 * @returns {moment.Moment|string} - Moment object or formatted date and time in the specified timezone, e.g. '2023-10-13T14:00:00+02:00'
 */
export function getDateTimeInDestinationTimezone(inputDate, timezone, returnString = '') {
  // check if string or moment object and set as moment object
  const date = (typeof inputDate === 'string') ? moment.tz(isValidMomentDate(inputDate), timezone) : inputDate.tz(timezone);

  // Return localized date as string
  if (returnString !== '') {
    return date.format(returnString);
  }

  // Return localized moment object
  return date;
}

/**
 * Function to get the date and time in the current timezone
 * @param {moment.Moment|string} inputDate - date to convert, e.g. '2023-10-13T12:00'
 * @param {string} returnString - returns formatted date and time if filled, e.g. 'LL' or 'DD.MM.YYYY HH:mm'
 * @returns {moment.Moment|string} - Moment object or formatted date and time in the current timezone, e.g. '2023-10-13T14:00:00+02:00'
 */
export function getDateTimeInCurrentTimezone(inputDate, returnString = '') {
  // check if string or moment object and set as moment object
  const date = (typeof inputDate === 'string') ? moment(isValidMomentDate(inputDate)).local(false) : inputDate.local(false);

  // Return localized date as string
  if (returnString !== '') {
    return date.format(returnString);
  }

  // Return localized moment object
  return date;
}

/**
 * Function to format currency
 *
 * @param {number} value - value to format
 * @param {string} currency - currency to use
 * @param {string} locale - locale to use
 *
 * @returns {string} - formatted currency
 * @example formatCurrency(1234567.89) // 1 234 568 kr
 */
export function formatCurrency(value, currency = 'NOK', locale = 'sv-SE') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Function to limit string length and return ... if it's too long
 *
 * @param {string} value - string to limit
 * @param {number} size - size to limit
 * @param {boolean} returnObject - if true, returns object with limited string and full string
 *
 * @returns {{stringAfterBreak: string, stringLimited: string, stringFull: string,
 * stringBeforeBreak: string, lengthError: [string]}} - returns object with limited string and full string if returnObject is true
 * @returns {string} - limited string if returnObject is false
 *
 * @example stringLimit('This is a long string', 10) // This is a...
 */
export function stringLimit(value, size, returnObject = false) {
  if (!value) {
    return '';
  }
  const text = value.toString();

  if (text.length <= size) {
    if (returnObject === true) {
      return {
        stringLimited: text,
        stringFull: text,
        stringBeforeBreak: text,
        stringAfterBreak: '',
        lengthError: `Length of string (${text.length}) is not more than given size (${size})`,
      };
    }

    return text;
  }

  if (returnObject === true) {
    return {
      stringLimited: `${text.substring(0, size)}...`,
      stringFull: text,
      stringBeforeBreak: text.substring(0, size),
      stringAfterBreak: text.substring(size),
    };
  }

  return `${text.substring(0, size)}...`;
}

/**
 * Function to get unique id based on current time
 * @param {string} prefix - prefix for unique id
 * @param {boolean} superUnique - if true, returns a super unique id
 * @returns {number|string} - unique id
 * @example uniqueId() // 1634123456789
 */
export function uniqueId(prefix = '', superUnique = false) {
  // If superUnique is true, generate a unique id with timestamp and random string
  if (superUnique) {
    const timestamp = Date.now();
    // Append a random alphanumeric string for additional uniqueness
    const randomString = Math.random().toString(36).slice(2, 11);

    return prefix.length > 0 ? `${prefix}-${timestamp}-${randomString}` : `${timestamp}-${randomString}`;
  }

  // If superUnique is false, generate a unique id with timestamp
  let id = (new Date()).getTime();

  // eslint-disable-next-line no-plusplus
  return prefix.length > 0 ? (prefix) + (id++) : id++;
}

/**
 * Function to check if object is empty
 * @param {object} obj - object to check
 * @returns {boolean} - true if object is empty, false if it's not
 * @example isEmptyObject({}) // true
 */
export function isEmptyObject(obj) {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
}

/**
 * Function to normalize string by removing accents and converting to lowercase
 * @param {string} str - string to normalize
 * @returns {string} - normalized string
 */
export function normalizeString(str) {
  return String(str).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Function to check if date is valid and return Date object
 * @param {string|Date} inputDate - date to check
 * @returns {string|Date} - valid date or error message
 */
export function valiDate(inputDate) {
  // Return empty string if inputDate is empty
  if (!inputDate) {
    throw new Error('Input date is empty');
  }
  if (typeof inputDate === 'boolean') {
    throw new Error('Invalid date: input is a boolean');
  }
  // Convert string to Date object if necessary
  const date = (typeof inputDate === 'string') ? new Date(inputDate) : inputDate;
  // Ensure the date is valid
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid date');
  }

  return date;
}

/**
 * Function to convert date string to time zone
 * @param {string} dateString - date string to convert
 * @param {string} timeZone - time zone to convert to
 * @returns {string} - converted date string
 * @example convertDateStringToTimeZone('2023-10-13T12:00:00Z', 'Europe/Oslo') // 2023-10-13T14:00:00+02:00
 */
export function convertDateStringToTimeZone(dateString, timeZone) {
  return moment(dateString).tz(timeZone).format();
}

/**
 * Function to check if date1 is same day as date2
 * @param {string|Date} date1 - date 1 to compare
 * @param {string|Date} date2 - date 2 to compare
 * @returns {boolean} - true if date1 is same day as date2, false if it's not
 * @example isSameDay(new Date('2023-10-13T12:00:00Z'), new Date('2023-10-13T12:00:00Z')) // true
 */
export function isSameDay(date1, date2) {
  const date1Obj = valiDate(date1);
  const date2Obj = valiDate(date2);

  return date1Obj.getFullYear() === date2Obj.getFullYear()
    && date1Obj.getMonth() === date2Obj.getMonth()
    && date1Obj.getDate() === date2Obj.getDate();
}

/**
 * Function to check if date1 is same month as date2
 * @param {string|Date} date1 - date 1 to compare
 * @param {string|Date} date2 - date 2 to compare
 * @returns {boolean} - true if date1 is same month as date2, false if it's not
 * @example isSameMonth(new Date('2023-10-13T12:00:00Z'), new Date('2023-10-15T12:00:00Z')) // true
 */
export function isSameMonth(date1, date2) {
  const date1Obj = valiDate(date1);
  const date2Obj = valiDate(date2);

  return date1Obj.getFullYear() === date2Obj.getFullYear()
    && date1Obj.getMonth() === date2Obj.getMonth();
}

/**
 * Function to get current date localized
 * @param { string } language - Language to use for formatting
 * @returns {string} - formatted date
 * @example currentDateLocalized('en') // 10/13/2023, 12:00:00 PM
 */
export function currentDateLocalized(language = 'nb') {
  const date = new Date();
  const dateOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  const timeOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };

  return `${date.toLocaleDateString(language, dateOptions)} ${date.toLocaleTimeString(language, timeOptions)}`;
}

/**
 * Function to convert inputDate to MySQL datetime format
 * @param {string|Date} inputDate - date to convert
 * @returns {string} - formatted date
 * @example formatToMySQLDateTime('2023-10-13T12:00:00Z') // 2023-10-13 12:00:00
 */
export function formatToMySQLDateTime(inputDate) {
  const date = valiDate(inputDate);

  // Check if the date string is in UTC ('Z' at the end) or if it's a Date object
  const isUTC = (typeof inputDate === 'string' && inputDate.endsWith('Z')) || inputDate instanceof Date;
  // Adjust for timezone offset if not UTC
  const adjustedDate = isUTC ? date : new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  // Helper function to pad numbers with leading zeros
  const pad = (number) => (number < 10 ? `0${number}` : number);

  // Format date into MySQL datetime format
  const year = isUTC ? adjustedDate.getUTCFullYear() : adjustedDate.getFullYear();
  const month = isUTC ? adjustedDate.getUTCMonth() : adjustedDate.getMonth();
  const day = isUTC ? adjustedDate.getUTCDate() : adjustedDate.getDate();
  const hours = isUTC ? adjustedDate.getUTCHours() : adjustedDate.getHours();
  const minutes = isUTC ? adjustedDate.getUTCMinutes() : adjustedDate.getMinutes();
  const seconds = isUTC ? adjustedDate.getUTCSeconds() : adjustedDate.getSeconds();

  return `${pad(year)}-${pad(month + 1)}-${pad(day)} ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Function to convert inputDate to locale custom datetime format
 * @param {string|Date} inputDate - date to convert
 * @param {string} locale - locale to use, e.g. 'nb-no'
 * @returns {string} - formatted date
 * @example formatDateTimeToLocaleCustomString('2023-10-13T12:00:00Z') // fre. 13/10 kl. 12:00
 */
export function formatDateTimeToLocaleCustomString(inputDate, locale = 'nb-no') {
  const date = valiDate(inputDate);

  // Create options for toLocaleString
  const options = {
    weekday: 'short', // narrow, short, long
    day: '2-digit', // numeric, 2-digit
    month: '2-digit', // numeric, 2-digit, long, short, narrow
    hour: '2-digit', // numeric, 2-digit
    minute: '2-digit', // numeric, 2-digit
    hourCycle: 'h23', // h11, h12, h23, h24
  };
  // Format the date to a locale string with options
  let dateString = date.toLocaleString(locale, options);
  // Add 'kl.' before the time part
  dateString = dateString.replace(/(\d+)\.(\d+)/, '$1/$2').replace('.,', ' kl.');

  return dateString;
}

/**
 * Function to convert inputDate to a localized date string in DD.MM.YYYY format
 * @param {string|Date} inputDate - The date to convert.
 * @returns {string} - The formatted date.
 * @example formatDateTimeToLocaleDateString('2023-10-13T12:00:00Z') // 13.10.2023
 */
export function formatDateTimeToLocaleDateString(inputDate) {
  const date = valiDate(inputDate);

  // Create options for toLocaleDateString
  const options = {
    day: '2-digit', // numeric, 2-digit
    month: '2-digit', // numeric, 2-digit, long, short, narrow
    year: 'numeric', // numeric, 2-digit
  };

  // Format the date to a locale string with options
  return date.toLocaleDateString('nb-NO', options);
}

/**
 * Format date to local ISO format.
 * @param {string | Date} inputDate - The date to format
 * @returns {string} - Formatted date
 * @example formatDateToLocalISO('2023-10-13T12:00:00Z') // 2023-10-13
 */
export function formatDateToLocalISO(inputDate) {
  const date = valiDate(inputDate);

  // Check if the date string is in UTC ('Z' at the end) or if it's a Date object
  const isUTC = (typeof inputDate === 'string' && inputDate.endsWith('Z')) || inputDate instanceof Date;
  // Adjust for timezone offset if not UTC
  const adjustedDate = isUTC ? date : new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  return adjustedDate.toISOString().split('T')[0];
}

/**
 * Format time to HH:mm.
 * @param {Date} inputDate - The date to format
 * @returns {string} - Formatted time
 * @example formatTime(new Date('2023-10-13T12:00:00Z')) // 12:00
 */
export function formatTime(inputDate) {
  const date = valiDate(inputDate);

  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
}

/*
  * Run these functions when imported
 */
initiateLanguageLocale();
